// exclusionCandidates — 荒らし復旧 第 2 段階「人を外して戻す」の pure ヘルパ
// (vandalism-recovery-ui-2026-09-27.md §3「詳細設計 (2026-10-01 /consult で合意)」)。
//
// 絵ログ (対象文書の系譜に絞ったもの) から
//   - 接続 (= uid) ごとの一覧 (表示名・時間帯・ストローク数・描いた量・消した量・
//     最大の太さ・最長ストローク)
//   - 荒らし検知 (`vandalism-detection-2026-09-27.md` §2) の条件に当たった接続の印
//   - 接続ごとのストローク (sid) 一覧
// を作り、外す対象 (uid / sid) を引いたイベント列 (ブラウザ replay 用) を返す。
//
// DOM / fetch に触れない純関数のみ。
//
// **サーバ側の検知との差**: サーバ (`vandalismJudgement.ts`) は「部屋に自分 1 人の
// ときに描いた」ストロークだけを数えるが、絵ログには在室情報 (member join/leave) が
// 無いのでこの近似はできない。ここでは全ストロークを数える (= 誤報多めの側。印は
// 「外す候補の初期チェック」であって確定ではなく、プレビューで必ず確認できる)。
// しきい値の値はサーバ定数と同じ (ずれたら `vandalismJudgement.ts` に合わせる)。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { SeqRange } from '../infrastructure/timelapse/segmentMerge.js';

// ── 検知しきい値 (server/src/domain/vandalismJudgement.ts と同値) ─────────────

export const DETECT_STAY_MS = 5 * 60_000;
export const DETECT_LONE_STROKE_LEN_PX = 5_000;
export const DETECT_ERASE_INK_THRESHOLD = 200_000;
export const DETECT_THICK_WIDTH_THRESHOLD = 50;
export const DETECT_THICK_NON_ERASER_LEN_PX = 2_000;

export type DetectionMarkId =
  | 'long-stroke'
  | 'erase-ink-total'
  | 'thick-eraser'
  | 'thick-non-eraser-long';

export interface DetectionMark {
  readonly id: DetectionMarkId;
  /** 画面に出す短い日本語 (数値付き)。 */
  readonly label: string;
}

/** ブラウザに重ねる軌跡 (オーバーレイ用) の最大点数。 */
const MAX_PATH_POINTS = 120;

export interface StrokeStat {
  readonly sid: string;
  readonly uid: string;
  readonly seq: number;
  readonly ts: number;
  readonly pen: string;
  readonly width: number;
  /** 折れ線長 (px)。 */
  readonly len: number;
  /** len × width — 「大きさ」の並べ替え / 表示に使う。 */
  readonly ink: number;
  readonly isEraser: boolean;
  /** オーバーレイ用に間引いた軌跡 (キャンバス座標)。 */
  readonly path: readonly { readonly x: number; readonly y: number }[];
}

export interface ConnectionStat {
  readonly uid: string;
  /** 使った表示名 (出現順・重複なし)。無ければ空。 */
  readonly displayNames: readonly string[];
  readonly firstTs: number;
  readonly lastTs: number;
  readonly firstSeq: number;
  readonly strokeCount: number;
  /** 消しゴム以外の len×width の合計。 */
  readonly drawInk: number;
  /** 消しゴムの len×width の合計。 */
  readonly eraseInk: number;
  readonly maxWidth: number;
  readonly longestStroke: number;
  /** 貼り付け画像の数。 */
  readonly imagepasteCount: number;
  /** 検知条件に当たったもの。空 = 印なし。 */
  readonly marks: readonly DetectionMark[];
  /** この接続のストローク (大きい順 = ink 降順)。 */
  readonly strokes: readonly StrokeStat[];
}

export interface ConnectionSummary {
  /** 新しい順 (lastTs 降順、同時刻は firstTs → firstSeq 降順)。最後の描画が新しい接続が上。 */
  readonly connections: readonly ConnectionStat[];
}

const pathLength = (pts: readonly { x: number; y: number }[]): number => {
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) {
    const a = pts[i - 1];
    const b = pts[i];
    if (a === undefined || b === undefined) continue;
    len += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return len;
};

const decimate = <T>(arr: readonly T[], max: number): readonly T[] => {
  if (arr.length <= max) return arr;
  const out: T[] = [];
  const step = (arr.length - 1) / (max - 1);
  for (let i = 0; i < max; i += 1) {
    const v = arr[Math.round(i * step)];
    if (v !== undefined) out.push(v);
  }
  return out;
};

const roundInk = (n: number): number => Math.round(n);

/** 検知条件 (サーバ `judgeVandalism` の in-room 近似) に当たった印を返す。 */
export const detectMarks = (c: {
  readonly stayMs: number;
  readonly eraseInk: number;
  readonly strokes: readonly Pick<StrokeStat, 'isEraser' | 'width' | 'len'>[];
}): readonly DetectionMark[] => {
  if (c.stayMs >= DETECT_STAY_MS) return [];
  if (c.strokes.length === 0) return [];
  const marks: DetectionMark[] = [];
  let maxLen = 0;
  for (const s of c.strokes) if (s.len > maxLen) maxLen = s.len;
  if (maxLen > DETECT_LONE_STROKE_LEN_PX) {
    marks.push({ id: 'long-stroke', label: `長さ ${Math.round(maxLen)}px のストローク` });
  }
  if (c.eraseInk > DETECT_ERASE_INK_THRESHOLD) {
    marks.push({ id: 'erase-ink-total', label: `消した量 ${roundInk(c.eraseInk)}` });
  }
  let thickEraser = 0;
  for (const s of c.strokes) {
    if (s.isEraser && s.width >= DETECT_THICK_WIDTH_THRESHOLD && s.width > thickEraser) {
      thickEraser = s.width;
    }
  }
  if (thickEraser > 0) marks.push({ id: 'thick-eraser', label: `太さ ${thickEraser} の消しゴム` });
  let thickLong: { width: number; len: number } | null = null;
  for (const s of c.strokes) {
    if (
      !s.isEraser &&
      s.width >= DETECT_THICK_WIDTH_THRESHOLD &&
      s.len > DETECT_THICK_NON_ERASER_LEN_PX &&
      (thickLong === null || s.len > thickLong.len)
    ) {
      thickLong = { width: s.width, len: s.len };
    }
  }
  if (thickLong !== null) {
    marks.push({
      id: 'thick-non-eraser-long',
      label: `太さ ${thickLong.width}・長さ ${Math.round(thickLong.len)}px の線`,
    });
  }
  return marks;
};

interface MutableConn {
  uid: string;
  names: string[];
  firstTs: number;
  lastTs: number;
  firstSeq: number;
  imagepasteCount: number;
}

interface MutableStroke {
  sid: string;
  uid: string;
  seq: number;
  ts: number;
  pen: string;
  width: number;
  pts: { x: number; y: number }[];
}

/**
 * 絵ログ (系譜に絞り込み済み) を接続ごとに集計する。取り消された (`stroke.cancelled`)
 * ストロークは結果に残らないので数えない。`whiteboard.restore` の印は集計対象外。
 */
export const aggregateConnections = (events: readonly StrokeLogEvent[]): ConnectionSummary => {
  const conns = new Map<string, MutableConn>();
  const strokes = new Map<string, MutableStroke>();
  const cancelled = new Set<string>();

  const touch = (uid: string, seq: number, ts: number): MutableConn => {
    let c = conns.get(uid);
    if (c === undefined) {
      c = { uid, names: [], firstTs: ts, lastTs: ts, firstSeq: seq, imagepasteCount: 0 };
      conns.set(uid, c);
    }
    if (ts < c.firstTs) c.firstTs = ts;
    if (ts > c.lastTs) c.lastTs = ts;
    if (seq < c.firstSeq) c.firstSeq = seq;
    return c;
  };

  for (const ev of events) {
    if (ev.t === 'whiteboard.restore') continue;
    // 文書先頭の全面クリア (全消し) は外しても結果が変わらない (replay が境界として残す)
    // ので、それだけをした人を接続として並べない。
    if (ev.t === 'whiteboard.clear' && ev.layerId === undefined) continue;
    const c = touch(ev.uid, ev.seq, ev.ts);
    switch (ev.t) {
      case 'stroke.start': {
        if (!c.names.includes(ev.displayName)) c.names.push(ev.displayName);
        strokes.set(ev.sid, {
          sid: ev.sid,
          uid: ev.uid,
          seq: ev.seq,
          ts: ev.ts,
          pen: ev.pen,
          width: ev.width,
          pts: [],
        });
        break;
      }
      case 'stroke.segment': {
        const s = strokes.get(ev.sid);
        if (s !== undefined) for (const p of ev.pts) s.pts.push({ x: p.x, y: p.y });
        break;
      }
      case 'stroke.cancelled':
        cancelled.add(ev.sid);
        break;
      case 'whiteboard.imagepaste':
      case 'whiteboard.region': // 範囲の移動・コピーも画像を置く操作として数える
        c.imagepasteCount += 1;
        break;
      default:
        break;
    }
  }

  const byUid = new Map<string, StrokeStat[]>();
  for (const s of strokes.values()) {
    if (cancelled.has(s.sid)) continue;
    const len = pathLength(s.pts);
    const stat: StrokeStat = {
      sid: s.sid,
      uid: s.uid,
      seq: s.seq,
      ts: s.ts,
      pen: s.pen,
      width: s.width,
      len,
      ink: len * s.width,
      isEraser: s.pen === 'eraser',
      path: decimate(s.pts, MAX_PATH_POINTS),
    };
    const list = byUid.get(s.uid);
    if (list === undefined) byUid.set(s.uid, [stat]);
    else list.push(stat);
  }

  const out: ConnectionStat[] = [];
  for (const c of conns.values()) {
    const list = [...(byUid.get(c.uid) ?? [])].sort((a, b) => b.ink - a.ink || a.seq - b.seq);
    let drawInk = 0;
    let eraseInk = 0;
    let maxWidth = 0;
    let longest = 0;
    for (const s of list) {
      if (s.isEraser) eraseInk += s.ink;
      else drawInk += s.ink;
      if (s.width > maxWidth) maxWidth = s.width;
      if (s.len > longest) longest = s.len;
    }
    out.push({
      uid: c.uid,
      displayNames: c.names,
      firstTs: c.firstTs,
      lastTs: c.lastTs,
      firstSeq: c.firstSeq,
      strokeCount: list.length,
      drawInk,
      eraseInk,
      maxWidth,
      longestStroke: longest,
      imagepasteCount: c.imagepasteCount,
      marks: detectMarks({ stayMs: c.lastTs - c.firstTs, eraseInk, strokes: list }),
      strokes: list,
    });
  }
  // 新しい順 (最後の描画が新しい接続が上)。荒らしは管理者が気づいた後の最新側にいることが多い。
  out.sort((a, b) => b.lastTs - a.lastTs || b.firstTs - a.firstTs || b.firstSeq - a.firstSeq);
  return { connections: out };
};

// ── 外す選択 ────────────────────────────────────────────────────────────────

export interface ExclusionSelection {
  readonly uids: ReadonlySet<string>;
  readonly sids: ReadonlySet<string>;
}

/** 印の付いた接続の uid (初期チェック / 一括トグル用)。 */
export const markedUids = (summary: ConnectionSummary): readonly string[] =>
  summary.connections.filter((c) => c.marks.length > 0).map((c) => c.uid);

/**
 * サーバに送る body 用の整形。uid を外す接続のストロークは uid に包含されるので
 * sid からは省く (payload を小さく / サーバの `excludedSids` 導出と二重にならない)。
 * 配列は安定順 (テスト・再現性のため)。
 */
export const buildExclusionPayload = (
  summary: ConnectionSummary,
  sel: ExclusionSelection,
): { readonly excludeUids: string[]; readonly excludeSids: string[] } => {
  const sidOwner = new Map<string, string>();
  for (const c of summary.connections) for (const s of c.strokes) sidOwner.set(s.sid, c.uid);
  const excludeUids = [...sel.uids].sort();
  const excludeSids = [...sel.sids]
    .filter((sid) => {
      const owner = sidOwner.get(sid);
      return owner === undefined || !sel.uids.has(owner);
    })
    .sort();
  return { excludeUids, excludeSids };
};

/** 外す対象があるか (確定ボタンの有効条件)。 */
export const hasExclusion = (sel: ExclusionSelection): boolean =>
  sel.uids.size > 0 || sel.sids.size > 0;

const sidOf = (ev: StrokeLogEvent): string | undefined =>
  ev.t === 'stroke.start' ||
  ev.t === 'stroke.segment' ||
  ev.t === 'stroke.end' ||
  ev.t === 'stroke.cancelled'
    ? ev.sid
    : undefined;

/**
 * 外す対象を引いたイベント列 (ブラウザ replay 用)。サーバ `planExclusion` の
 * `isExcluded` と同じ規則: 外す uid のイベント全部 + 外す sid のイベント全部
 * (`stroke.start` を外す uid が始めた sid は丸ごと)。
 *
 * **例外 (サーバと違う点)**: 全面 `whiteboard.clear` と `whiteboard.restore` は
 * 残す。replay の文書境界判定 (`lineageSeed`) が境界イベントを見て土台を決めるので、
 * 外す uid の全消しを引くと前の文書が土台に混ざってしまう。全面 clear は文書の
 * 先頭にしか現れない (途中に来れば別の文書になる) ので、残しても「系譜の先頭で
 * 白紙化する」だけで結果は変わらない。
 */
export const filterExcludedEvents = (
  events: readonly StrokeLogEvent[],
  sel: ExclusionSelection,
): readonly StrokeLogEvent[] => {
  if (!hasExclusion(sel)) return events;
  const excludedSids = new Set(sel.sids);
  for (const ev of events) {
    if (ev.t === 'stroke.start' && sel.uids.has(ev.uid)) excludedSids.add(ev.sid);
  }
  return events.filter((ev) => {
    if (ev.t === 'whiteboard.restore') return true;
    if (ev.t === 'whiteboard.clear' && ev.layerId === undefined) return true;
    if (sel.uids.has(ev.uid)) return false;
    const sid = sidOf(ev);
    return !(sid !== undefined && excludedSids.has(sid));
  });
};

// ── 文書の終端 / 絵ログの穴 ─────────────────────────────────────────────────

const isBoundary = (ev: StrokeLogEvent): boolean =>
  (ev.t === 'whiteboard.clear' && ev.layerId === undefined) || ev.t === 'whiteboard.restore';

/**
 * 過去キャンバス行の `clearedSeq` は「全消し / 復元の瞬間の seq」= たいてい境界
 * イベント自身なので、その行の文書は 1 つ手前で終わる (サーバ `normalizeEndSeq` と
 * 同じ規則 — 揃えておく)。
 */
export const normalizeEndSeq = (events: readonly StrokeLogEvent[], endSeq: number): number => {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i];
    if (ev === undefined || ev.seq > endSeq) continue;
    if (ev.seq < endSeq) return endSeq;
    return isBoundary(ev) ? endSeq - 1 : endSeq;
  }
  return endSeq;
};

/** `ranges` のうち `coverage` に載っていない seq 区間 (= 絵ログの穴)。 */
export const findRangeGaps = (
  coverage: readonly SeqRange[],
  ranges: readonly SeqRange[],
): readonly SeqRange[] => {
  const sorted = [...coverage].sort((a, b) => a.fromSeq - b.fromSeq);
  const gaps: SeqRange[] = [];
  for (const r of ranges) {
    let cursor = r.fromSeq;
    for (const c of sorted) {
      if (c.toSeq < cursor) continue;
      if (c.fromSeq > r.toSeq) break;
      if (c.fromSeq > cursor)
        gaps.push({ fromSeq: cursor, toSeq: Math.min(c.fromSeq - 1, r.toSeq) });
      cursor = Math.max(cursor, c.toSeq + 1);
      if (cursor > r.toSeq) break;
    }
    if (cursor <= r.toSeq) gaps.push({ fromSeq: cursor, toSeq: r.toSeq });
  }
  return gaps;
};

// ── 表示用の整形 ────────────────────────────────────────────────────────────

/** 1 万以上は「12.3万」、それ未満は整数。 */
export const formatAmount = (n: number): string =>
  n >= 10_000 ? `${(n / 10_000).toFixed(1)}万` : `${Math.round(n)}`;

/** 滞在 (ms) → 「3分20秒」/「45秒」。 */
export const formatStay = (ms: number): string => {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}分${s}秒` : `${s}秒`;
};

// ── 確定 job の文言 ─────────────────────────────────────────────────────────

const PHASE_LABELS: Record<string, string> = {
  queued: '順番を待っています…',
  'forcing-snapshot': '今の絵を保存しています…',
  'loading-log': '絵ログを読み込んでいます…',
  planning: '外す範囲を計算しています…',
  'loading-base': '土台の絵を読み込んでいます…',
  baking: '外した版を作っています…',
  publishing: '保存しています…',
};

export const exclusionPhaseLabel = (phase: string): string =>
  PHASE_LABELS[phase] ?? '処理しています…';

const FAILURE_MESSAGES: Record<string, string> = {
  log_gap: '絵ログに抜けがあるため戻せません',
  no_base_generation: '外す線より前の保存点が見つからないため戻せません',
  log_too_large: '絵ログが大きすぎて戻せません (範囲が長すぎます)',
  log_unavailable: '絵ログを読み込めませんでした。しばらくしてからもう一度お試しください',
  generations_unavailable:
    '保存点の一覧を読み込めませんでした。しばらくしてからもう一度お試しください',
  base_fetch_failed: '土台の絵を読み込めませんでした。しばらくしてからもう一度お試しください',
  bake_failed: '外した版を作れませんでした (処理が時間内に終わりませんでした)',
  publish_failed: '外した版を保存できませんでした。しばらくしてからもう一度お試しください',
  record_failed: '外した版を過去キャンバスに登録できませんでした',
  nothing_to_exclude: '外す対象がこの文書に見つかりませんでした',
  target_not_supported: 'この過去キャンバスは外した版なので、さらに外すことはできません',
  past_canvas_not_found: '対象の過去キャンバスが見つかりません (削除された可能性があります)',
  room_not_found: 'この部屋が見つかりませんでした',
  no_snapshot_session: 'この部屋にはまだ保存された絵がありません',
  internal_error: '予期しないエラーが起きました。もう一度お試しください',
};

export const exclusionFailureMessage = (code: string): string =>
  FAILURE_MESSAGES[code] ?? `外した版を作れませんでした (${code})`;

export const EXCLUSION_START_ERROR_MESSAGES = {
  unauthorized: '権限がありません。オーナーとしてログインし直してください',
  'not-available': 'この部屋では「人を外して戻す」を使えません',
  busy: '別の復旧処理中です。しばらくしてからもう一度お試しください',
  invalid: '指定が正しくありません。画面を開き直してください',
  error: '開始できませんでした。通信環境を確認して再度お試しください',
} as const;

export const EXCLUSION_JOB_LOST_MESSAGE =
  '処理の状態が分からなくなりました (サーバーが再起動した可能性があります)。もう一度実行してください';
export const EXCLUSION_POLL_ERROR_MESSAGE =
  '状態を確認できませんでした。通信環境を確認して、もう一度実行してください';
